'use client'

import { type RefObject, useCallback, useEffect, useRef, useState } from 'react'

// ── Constants ──────────────────────────────────────────────────────────────────
const MOBILE_BREAKPOINT = 768
const TABLET_BREAKPOINT = 1024
const DEFAULT_DPR_CAP: Record<DeviceTier, number> = { mobile: 1.5, tablet: 2, desktop: 2.5 }
const DEFAULT_THROTTLE_MS: Record<DeviceTier, number> = { mobile: 33, tablet: 0, desktop: 0 }
const DEFAULT_ROOT_MARGIN = '200px'

// ── Types ──────────────────────────────────────────────────────────────────────
export type DeviceTier = 'mobile' | 'tablet' | 'desktop'

export interface CanvasAnimationMeta {
  /** CSS pixel width of the container */
  readonly w: number
  /** CSS pixel height of the container */
  readonly h: number
  /** Effective devicePixelRatio actually applied (after the per-tier cap) */
  readonly dpr: number
  /** Device tier sampled at mount */
  readonly tier: DeviceTier
}

export interface UseCanvasAnimationOptions {
  /**
   * Draw a frame. Called on every rAF tick while the canvas is visible, the
   * tab is active, and (if reduced motion is set) once at mount.
   */
  onFrame: (ctx: CanvasRenderingContext2D, meta: CanvasAnimationMeta) => void
  /**
   * Optional resize callback. Fires once on mount and on every container
   * resize, before the next onFrame.
   */
  onResize?: (ctx: CanvasRenderingContext2D, meta: CanvasAnimationMeta) => void
  /**
   * DPR cap per tier. Merged with defaults { desktop: 2.5, tablet: 2, mobile: 1.5 }.
   */
  dprCap?: Partial<Record<DeviceTier, number>>
  /**
   * Minimum ms between onFrame calls per tier. 0 = every rAF tick. Merged with
   * defaults { desktop: 0, tablet: 0, mobile: 33 } (~30fps on mobile).
   */
  throttleMs?: Partial<Record<DeviceTier, number>>
  /**
   * IntersectionObserver rootMargin. Default '200px'.
   */
  rootMargin?: string
}

export interface CanvasAnimationHandle {
  canvasRef: RefObject<HTMLCanvasElement | null>
  containerRef: RefObject<HTMLDivElement | null>
  tier: DeviceTier
  reducedMotion: boolean
  /** Force a single static repaint with the current ctx/meta. Useful when the rAF loop is paused (reduced motion, static-on-mobile) and an external event like a theme change requires a repaint. */
  redraw: () => void
}

// ── SSR-safe initializers ──────────────────────────────────────────────────────
// Client components still run during SSR prerender for initial HTML. Return
// safe defaults on the server; the real values are picked up on the client
// mount pass when React calls the useState initializer again.
function getTier(): DeviceTier {
  if (typeof window === 'undefined') return 'desktop'
  const w = window.innerWidth
  if (w < MOBILE_BREAKPOINT) return 'mobile'
  if (w < TABLET_BREAKPOINT) return 'tablet'
  return 'desktop'
}

function getReducedMotion(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

// ── Hook ───────────────────────────────────────────────────────────────────────
export function useCanvasAnimation(options: UseCanvasAnimationOptions): CanvasAnimationHandle {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  // Sampled once at mount — rotation/resize between tiers handled by full reload
  const [tier] = useState<DeviceTier>(() => getTier())
  const [reducedMotion] = useState<boolean>(() => getReducedMotion())

  // Keep callbacks fresh without re-running the main effect
  // Updated in an effect rather than during render to satisfy react-hooks/refs
  const optionsRef = useRef(options)
  useEffect(() => {
    optionsRef.current = options
  })

  // NOTE: dprCap, throttleMs, and rootMargin are captured at mount and do NOT respond to
  // mid-session prop changes — only onFrame and onResize do (through optionsRef).
  const redrawRef = useRef<() => void>(() => {})

  useEffect(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // ── Effective DPR (tier-capped) ────────────────────────────────────────────
    const capForTier = { ...DEFAULT_DPR_CAP, ...optionsRef.current.dprCap }
    const dpr = Math.min(window.devicePixelRatio || 1, capForTier[tier])

    // ── Throttle config ────────────────────────────────────────────────────────
    const throttleConfig = { ...DEFAULT_THROTTLE_MS, ...optionsRef.current.throttleMs }
    const frameThrottleMs = throttleConfig[tier]

    // ── Stable meta object (mutated on resize, not reallocated) ───────────────
    const meta = { w: 0, h: 0, dpr, tier } as unknown as {
      w: number
      h: number
      dpr: number
      tier: DeviceTier
    }

    // Wire up the redraw handle now that ctx + meta are ready
    redrawRef.current = () => {
      optionsRef.current.onFrame(ctx, meta as unknown as CanvasAnimationMeta)
    }

    // ── Resize handler — re-sizes canvas and calls onResize ───────────────────
    const applyResize = () => {
      const rect = container.getBoundingClientRect()
      meta.w = rect.width
      meta.h = rect.height
      canvas.width = Math.floor(meta.w * dpr)
      canvas.height = Math.floor(meta.h * dpr)
      canvas.style.width = `${meta.w}px`
      canvas.style.height = `${meta.h}px`
      optionsRef.current.onResize?.(ctx, meta as unknown as CanvasAnimationMeta)
      // Reduced motion has no loop — repaint here so canvas.width= doesn't leave it blank.
      if (reducedMotion) optionsRef.current.onFrame(ctx, meta as unknown as CanvasAnimationMeta)
    }

    applyResize()

    const ro = new ResizeObserver(applyResize)
    ro.observe(container)

    // ── Reduced motion: one static frame, no loop ─────────────────────────────
    if (reducedMotion) {
      return () => {
        redrawRef.current = () => {}
        ro.disconnect()
      }
    }

    // ── rAF loop state ─────────────────────────────────────────────────────────
    let rafId = 0
    let stopped = false
    let isIntersecting = false
    let isTabVisible = !document.hidden
    let lastDrawTimestamp = -Infinity

    const startLoop = () => {
      if (rafId !== 0 || stopped) return
      const tick = (timestamp: number) => {
        if (stopped) return
        // Throttle: skip onFrame call if under the threshold, but keep the loop alive
        if (frameThrottleMs === 0 || timestamp - lastDrawTimestamp >= frameThrottleMs) {
          lastDrawTimestamp = timestamp
          optionsRef.current.onFrame(ctx, meta as unknown as CanvasAnimationMeta)
        }
        rafId = requestAnimationFrame(tick)
      }
      rafId = requestAnimationFrame(tick)
    }

    const stopLoop = () => {
      if (rafId !== 0) {
        cancelAnimationFrame(rafId)
        rafId = 0
      }
    }

    const maybeStart = () => {
      if (isIntersecting && isTabVisible) startLoop()
      else stopLoop()
    }

    // ── IntersectionObserver — pause when off-screen ──────────────────────────
    const rootMargin = optionsRef.current.rootMargin ?? DEFAULT_ROOT_MARGIN
    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1]
        if (entry) isIntersecting = entry.isIntersecting
        maybeStart()
      },
      { rootMargin }
    )
    io.observe(container)

    // ── Tab visibility — pause when backgrounded ──────────────────────────────
    const onVisibilityChange = () => {
      isTabVisible = !document.hidden
      maybeStart()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    // ── Cleanup ────────────────────────────────────────────────────────────────
    return () => {
      stopped = true
      stopLoop()
      redrawRef.current = () => {}
      ro.disconnect()
      io.disconnect()
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [tier, reducedMotion])

  const redraw = useCallback(() => redrawRef.current(), [])

  return { canvasRef, containerRef, tier, reducedMotion, redraw }
}
